import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  LinearProgress,
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import styles, {
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../styles/styles";
import { useDispatch, useSelector } from "react-redux";
import { backend_api } from "../../../static/server";
import {
  getCaseByid,
  getPracticeArea,
} from "../../../redux/Apps/Lawyerapp/CaseSlice/CaseSlice";
import { useParams, useNavigate } from "react-router-dom";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import { KeyboardArrowDownRounded } from "@mui/icons-material";
import UserAvatar from "../../../components/UserAvatar";
import { getContactDetail } from "../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { getActivities } from "../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import { getAllBilles } from "../../../redux/Apps/Lawyerapp/BillingSlice/BillingSlice";
import BillGenerateForm from "../../../components/LawyerCompo/Billing/BillGenrateForm";
import NewTransactionForm from "../../../components/LawyerCompo/Accounts/AccountsForms/NewTransactionForm";
import { useDialog } from "../../../components/DialogContext";
import {
  ContentCopy,
  Phone,
  Email,
  LocationOn,
  Person,
} from "@mui/icons-material";
import ExpenseForm from "../../../components/LawyerCompo/Activities/ActivitiesForms/ExpenseForm";
import ServiceTimeForm from "../../../components/LawyerCompo/Activities/ActivitiesForms/ServiceTimeForm";

const CustomAccordion = ({ expanded, onChange, title, children }) => (
  <Accordion
    disableGutters
    elevation={0}
    square
    expanded={expanded}
    onChange={onChange}
    sx={{
      borderRadius: "0.375rem",
      border: "0.0625rem solid #e5e5e5",
      backgroundColor: "#fff",
      boxShadow: "none",
      "&::before": { display: "none" },
    }}
  >
    <AccordionSummary
      expandIcon={<KeyboardArrowDownRounded />}
      sx={{
        padding: "0.75rem 1rem",
        borderBottom: expanded ? "0.0625rem solid #e5e5e5" : "none",
        minHeight: "unset",
        "& .MuiAccordionSummary-content": {
          margin: "0rem",
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
        },
      }}
    >
      {typeof title === "string" ? (
        <Typography
          variant="body1"
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {title}
        </Typography>
      ) : (
        title
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ padding: "0rem", pt: "0.5rem" }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

const CaseDashboard = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { token } = useSelector((data) => data.user);
  const navigate = useNavigate();
  const { showDialog } = useDialog();
  const { caseData, loading } = useSelector((state) => state.case);
  const [budgetExpanded, setBudgetExpanded] = useState(true);
  const [contactExpand, setContactExpand] = useState(true);
  const [matterExpand, setMatterExpand] = useState(true);
  const [contactDetailExpand, setContactDetailExpand] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { allActivities } = useSelector((state) => state.activity);
  const { allBilles } = useSelector((state) => state.billing);

  const quickBillRows = useMemo(() => {
    if (!Array.isArray(allActivities)) return [];
    return allActivities
      .filter((item) => !item.bill_genrated && !item.non_billable)
      .map((item) => {
        let duration = "-";
        if (item.type === "time" && item.time_in && item.time_out) {
          const diffMs = new Date(item.time_out) - new Date(item.time_in);
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffMinutes = Math.floor(
            (diffMs % (1000 * 60 * 60)) / (1000 * 60)
          );
          duration = `${diffHours}h ${diffMinutes}m`;
        }

        return {
          id: item.id,
          user:
            (item.lawyer?.first_name || "") +
            " " +
            (item.lawyer?.last_name || ""),
          type: item.type,
          description: item.description,
          matter_name: item.case?.case_name
            ? generateUniqueMatterName(item.case.id, item.case.case_name)
            : "-",
          quantity: item.type === "time" ? duration : item.quantity,
          rate: item.hourly_rate,
          non_billable_amount: item.non_billable ? item.total_payment : "-",
          billable_amount: item.non_billable ? "-" : item.total_payment,
          create_at: item.created_at,
          category_name: item.category?.name,
          bill_entry_show: item.bill_entry_show,
          time_in: item.time_in,
          time_out: item.time_out,
          hourly_rate: item.hourly_rate,
          is_paid: item.is_paid,
          non_billable: item.non_billable,
          case: item.case?.id,
          category: item.category?.id,
          client: item.case?.case_related_person?.id,
          bill_genrated: item.bill_genrated,
        };
      });
  }, [allActivities]);

  useEffect(() => {
    dispatch(
      getCaseByid({ url: `${backend_api}case/register/${id}/`, token: token })
    );

    dispatch(
      getPracticeArea({
        url: `${backend_api}case/case-type/?case_service_type=practice_area`,
        token: token,
      })
    );
    dispatch(
      getActivities({
        url: `${backend_api}firm-case/expense/?case=${id}`,
        token: token,
      })
    );
    dispatch(
      getAllBilles({
        url: `${backend_api}firm-case/bill/?case=${id}`,
        token: token,
      })
    );
  }, [dispatch, id, token]);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setSnackbarOpen(true);
      setTimeout(() => setCopiedField(""), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const CopyIconButton = ({ text, fieldName }) => (
    <Tooltip
      title={copiedField === fieldName ? "Copied!" : "Copy to clipboard"}
    >
      <IconButton
        size="small"
        onClick={() => copyToClipboard(text, fieldName)}
        sx={{
          ml: 0.5,
          color: copiedField === fieldName ? "success.main" : "grey.400",
          "&:hover": { color: "primary.main" },
          p: "0.25rem",
        }}
      >
        <ContentCopy sx={{ fontSize: "0.875rem" }} />
      </IconButton>
    </Tooltip>
  );

  const formatCurrency = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return "$0.00";
    return `$${num.toFixed(2)}`;
  };

  const handleQuickBill = () => {
    if (quickBillRows.length === 0) return;
    showDialog(<BillGenerateForm entryData={quickBillRows} />, {
      dialogTitle: "Draft Bill",
    });
  };

  const handleViewBills = () => {
    navigate(`/dashboard/bills?case=${id}`);
  };

  const handleNewRequest = () => {
    showDialog(
      <NewTransactionForm
        accountType="client fund"
        ClientAccountId={caseData?.case_related_person?.id}
      />,
      { dialogTitle: "New Transaction" }
    );
  };

  const formatFieldValue = (field) => {
    if (!field) return "";
    const { type, value, pickList } = field;

    if (type === "pick_list") {
      if (pickList?.options) {
        const opt = pickList.options.find((o) => o.value === value);
        if (opt) return opt.label;
      }

      if (pickList?.link_option) {
        if (typeof value === "object" && value !== null) {
          if (value.first_name || value.last_name)
            return `${value.first_name || ""} ${value.last_name || ""}`.trim();
          return (
            value.name || value.case_name || value.label || value.value || ""
          );
        }
        if (typeof value === "string") return value.replace(/_/g, " ");
      }

      if (typeof value === "string") return value.replace(/_/g, " ");
    }

    if (typeof value === "string" && value.startsWith("option_")) {
      return value.replace(/_/g, " ");
    }

    return value;
  };

  const InfoRow = ({
    label,
    value,
    fieldName,
    icon: Icon,
    color = "grey.600",
  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1,
        px: 1,
        "&:not(:last-child)": {
          borderBottom: "0.0625rem solid #f0f0f0",
        },
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}>
        {Icon && <Icon sx={{ color, mr: 1, fontSize: "1rem" }} />}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: "grey.700",
            minWidth: "8rem",
            flexShrink: 0,
            fontSize: "0.875rem",
          }}
        >
          {label}:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "grey.900",
            ml: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            fontSize: "0.875rem",
          }}
          title={value || "Not specified"}
        >
          {value || "Not specified"}
        </Typography>
      </Box>
      {value && <CopyIconButton text={value} fieldName={fieldName} />}
    </Box>
  );

  const nonBillableAmount = useMemo(() => {
    if (!Array.isArray(allActivities)) return 0;
    return allActivities
      .filter((item) => item.non_billable && !item.bill_genrated)
      .reduce((sum, item) => sum + parseFloat(item.total_payment || 0), 0);
  }, [allActivities]);

  const wipAmount = useMemo(() => {
    if (!Array.isArray(allActivities)) return 0;
    return allActivities
      .filter((item) => !item.bill_genrated && !item.non_billable)
      .reduce((sum, item) => sum + parseFloat(item.total_payment || 0), 0);
  }, [allActivities]);

  const totalBilled = useMemo(() => {
    if (!Array.isArray(allBilles)) return 0;
    return allBilles.reduce(
      (sum, bill) => sum + parseFloat(bill.total_amount || 0),
      0
    );
  }, [allBilles]);

  const totalCollected = useMemo(() => {
    if (!Array.isArray(allBilles)) return 0;
    return allBilles.reduce(
      (sum, bill) => sum + parseFloat(bill.payment_received || 0),
      0
    );
  }, [allBilles]);

  const outstandingBalance = useMemo(() => {
    return totalBilled - totalCollected;
  }, [totalBilled, totalCollected]);

  const matterTrustFunds = useMemo(() => {
    return (
      parseFloat(caseData?.trust_deposits || 0) -
      parseFloat(caseData?.trust_applied || 0)
    );
  }, [caseData]);

  const minTrustHold = useMemo(() => {
    return parseFloat(caseData?.minimum_trust_hold || 5000);
  }, [caseData]);

  const matterBudget = useMemo(
    () => parseFloat(caseData?.budget || 0),
    [caseData]
  );

  const usedBudgetValue = useMemo(
    () => totalBilled + wipAmount,
    [totalBilled, wipAmount]
  );

  const usedBudgetPercent = useMemo(() => {
    if (!matterBudget) return 0;
    const percent = (usedBudgetValue / matterBudget) * 100;
    return percent > 100 ? 100 : percent;
  }, [usedBudgetValue, matterBudget]);

  const timeEntries = useMemo(() => {
    if (!Array.isArray(allActivities)) return [];
    return allActivities.filter((a) => a.type === "time");
  }, [allActivities]);

  const totalTimeHours = useMemo(() => {
    return timeEntries.reduce((sum, item) => {
      if (item.time_in && item.time_out) {
        const diff = new Date(item.time_out) - new Date(item.time_in);
        return sum + diff / 3600000;
      }
      return sum;
    }, 0);
  }, [timeEntries]);

  const unbilledTimeHours = useMemo(() => {
    return timeEntries.reduce((sum, item) => {
      if (!item.bill_genrated && item.time_in && item.time_out) {
        const diff = new Date(item.time_out) - new Date(item.time_in);
        return sum + diff / 3600000;
      }
      return sum;
    }, 0);
  }, [timeEntries]);

  const timeValueTotal = useMemo(() => {
    return timeEntries.reduce(
      (sum, item) => sum + parseFloat(item.total_payment || 0),
      0
    );
  }, [timeEntries]);

  const expenseEntries = useMemo(() => {
    if (!Array.isArray(allActivities)) return [];
    return allActivities.filter((a) => a.type === "expense");
  }, [allActivities]);

  const totalExpenses = useMemo(() => {
    return expenseEntries.reduce(
      (sum, item) => sum + parseFloat(item.total_payment || 0),
      0
    );
  }, [expenseEntries]);

  const unbilledExpenses = useMemo(() => {
    return expenseEntries.reduce((sum, item) => {
      if (!item.bill_genrated && !item.non_billable) {
        return sum + parseFloat(item.total_payment || 0);
      }
      return sum;
    }, 0);
  }, [expenseEntries]);

  const financialData = useMemo(() => {
    return [
      {
        title: "Work in progress ",
        num: wipAmount,
        btnText: "Quick bill",
        unbilled: formatCurrency(wipAmount),
        nonBillable: formatCurrency(nonBillableAmount),
        draft: "0.00",
      },
      {
        title: "Outstanding balance ",
        num: outstandingBalance,
        btnText: "view billes",
      },
      {
        title: "Client trust funds",
        num: matterTrustFunds,
        btnText: "New request",
        minTrustHold: formatCurrency(minTrustHold),
      },
      {
        title: "Matter Budget",
        num: matterBudget,
        usedBudget: usedBudgetPercent,
      },
    ];
  }, [
    wipAmount,
    nonBillableAmount,
    outstandingBalance,
    matterTrustFunds,
    minTrustHold,
    matterBudget,
    usedBudgetPercent,
  ]);

  const customFieldSections = useMemo(() => {
    if (!Array.isArray(caseData?.custom_fields_data)) return [];
    return caseData.custom_fields_data.map((sectionObj) => {
      const name = Object.keys(sectionObj)[0];
      return { sectionName: name, fields: sectionObj[name] || [] };
    });
  }, [caseData]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          <div className="bg-white rounded-lg items-center shadow-md p-2">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <h2
                  className={`${styles.title}`}
                  style={{ fontSize: "1.125rem" }}
                >
                  {generateUniqueMatterName(caseData?.id, caseData?.case_name)}
                </h2>
                <Typography
                  variant="body1"
                  sx={{
                    p: "0.2rem 0.5rem",
                    borderRadius: "1rem",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    width: "5rem",
                    bgcolor:
                      caseData?.case_status === "open"
                        ? "#4CAF50"
                        : caseData?.case_status === "pending"
                        ? "#FFC107"
                        : "#F44336",
                    color: "#fff",
                  }}
                >
                  {caseData?.case_status}
                </Typography>
              </Box>
              <Box className="flex gap-4">
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: "capitalize",
                    fontSize: "0.75rem",
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  Edit Matter
                </Button>
              </Box>
            </Box>
          </div>
          <div>
            <div className="flex items-start justify-between w-full ">
              <div className="w-full">
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  <Box
                    className="bg-white rounded-lg h-[80vh] p-2 w-full shadow-sm"
                    sx={{
                      ...pageScrollStyle,
                      overflow: "auto",
                      p: "0.75rem",
                      display: "flex",
                      gap: "0.75rem",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                        width: "65%",
                      }}
                    >
                      <Box>
                        <CustomAccordion
                          expanded={budgetExpanded}
                          onChange={() => setBudgetExpanded(!budgetExpanded)}
                          title={`Financial Record of ${generateUniqueMatterName(
                            caseData?.id,
                            caseData?.case_name
                          )}`}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "1rem",
                              p: "0.5rem",
                            }}
                          >
                            {/* Financial Data Grid */}
                            <Box sx={{ display: "flex", gap: "0.75rem" }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Grid container spacing={1.5}>
                                  {financialData.map((item, i) => (
                                    <Grid key={i} item xs={6} md={6}>
                                      <Box
                                        className="bg-white rounded-md shadow-md"
                                        sx={{
                                          border: "0.0625rem solid #e5e5e5",
                                          borderRadius: "0.375rem",
                                          p: "0.5rem",
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "0.375rem",
                                          height: "100%",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            mb: "0.25rem",
                                          }}
                                        >
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              flex: 1,
                                              mr: "0.5rem",
                                            }}
                                          >
                                            {item.title}
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontWeight: 600,
                                              fontSize: "0.875rem",
                                              flexShrink: 0,
                                            }}
                                          >
                                            {formatCurrency(item.num)}
                                          </Typography>
                                        </Box>

                                        {item.usedBudget && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "0.25rem",
                                              mb: "0.25rem",
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                gap: "0.25rem",
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: "#999",
                                                  fontSize: "0.75rem",
                                                }}
                                              >
                                                Used Budget:
                                              </Typography>
                                              <Typography
                                                variant="body2"
                                                sx={{ fontSize: "0.75rem" }}
                                              >
                                                {`${item.usedBudget.toFixed(
                                                  2
                                                )}%`}
                                              </Typography>
                                            </Box>
                                            <LinearProgress
                                              variant="determinate"
                                              value={item.usedBudget}
                                              sx={{
                                                height: "0.375rem",
                                                borderRadius: "0.1875rem",
                                                backgroundColor: "#f0f0f0",
                                                "& .MuiLinearProgress-bar": {
                                                  backgroundColor: "#1e4620",
                                                },
                                              }}
                                            />
                                          </Box>
                                        )}

                                        {item.unbilled && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              gap: "0.25rem",
                                              mb: "0.25rem",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: "#999",
                                                fontSize: "0.75rem",
                                              }}
                                            >
                                              Unbilled:
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontSize: "0.75rem" }}
                                            >
                                              {item.unbilled}
                                            </Typography>
                                          </Box>
                                        )}

                                        {item.nonBillable && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              gap: "0.25rem",
                                              mb: "0.25rem",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: "#999",
                                                fontSize: "0.625rem",
                                              }}
                                            >
                                              Non-billable:
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontSize: "0.625rem" }}
                                            >
                                              {item.nonBillable}
                                            </Typography>
                                          </Box>
                                        )}

                                        {item.btnText && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "flex-start",
                                              mt: "auto",
                                            }}
                                          >
                                            <Button
                                              variant="outlined"
                                              size="small"
                                              sx={{
                                                textTransform: "capitalize",
                                                fontSize: "0.75rem",
                                                borderRadius: "0.25rem",
                                                px: 1,
                                                py: 0.25,
                                              }}
                                              onClick={() => {
                                                if (
                                                  item.title.includes("Work")
                                                ) {
                                                  handleQuickBill();
                                                } else if (
                                                  item.title.includes(
                                                    "Outstanding"
                                                  )
                                                ) {
                                                  handleViewBills();
                                                } else if (
                                                  item.title.includes(
                                                    "Client trust"
                                                  )
                                                ) {
                                                  handleNewRequest();
                                                }
                                              }}
                                            >
                                              {item.btnText}
                                            </Button>
                                          </Box>
                                        )}
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            </Box>

                            {/* Time Tracking and Expenses Cards */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: "1rem",
                                flexWrap: "wrap",
                              }}
                            >
                              {/* Time Tracking Card */}
                              <Box
                                className="bg-white rounded-md shadow-md"
                                sx={{
                                  border: "0.0625rem solid #e5e5e5",
                                  borderRadius: "0.375rem",
                                  p: "0.75rem",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.75rem",
                                  flex: 1,
                                  minWidth: "300px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Time Tracking
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    sx={{
                                      textTransform: "capitalize",
                                      fontSize: "0.75rem",
                                      px: 1,
                                      py: 0.25,
                                    }}
                                    onClick={() => {
                                      showDialog(<ServiceTimeForm />, {
                                        dialogTitle: "New time entry",
                                      });
                                    }}
                                  >
                                    Add Time
                                  </Button>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      totalTimeHours > 0
                                        ? (unbilledTimeHours / totalTimeHours) *
                                          100
                                        : 0
                                    }
                                    sx={{
                                      height: "0.375rem",
                                      borderRadius: "0.1875rem",
                                      backgroundColor: "#f0f0f0",
                                      "& .MuiLinearProgress-bar": {
                                        backgroundColor: "#1e4620",
                                      },
                                    }}
                                  />

                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "0.25rem",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          fontWeight: 500,
                                          color: "text.secondary",
                                        }}
                                      >
                                        Total Time:
                                      </Typography>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          fontWeight: 500,
                                          color: "#666",
                                        }}
                                      >
                                        {`${totalTimeHours.toFixed(2)} Hours`}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          fontWeight: 500,
                                          color: "text.secondary",
                                        }}
                                      >
                                        Unbilled Time:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          fontWeight: 500,
                                          color: "#666",
                                        }}
                                      >
                                        {`${unbilledTimeHours.toFixed(
                                          2
                                        )} Hours`}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          fontWeight: 500,
                                          color: "text.secondary",
                                        }}
                                      >
                                        Total Amount:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.6875rem",
                                          fontWeight: 500,
                                          color: "#666",
                                        }}
                                      >
                                        {formatCurrency(timeValueTotal)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>

                              {/* Expenses Card */}
                              <Box
                                className="bg-white rounded-md shadow-md"
                                sx={{
                                  border: "0.0625rem solid #e5e5e5",
                                  borderRadius: "0.375rem",
                                  p: "0.75rem",
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "0.75rem",
                                  flex: 1,
                                  minWidth: "300px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Expenses
                                  </Typography>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    sx={{
                                      textTransform: "capitalize",
                                      fontSize: "0.75rem",
                                      px: 1,
                                      py: 0.25,
                                    }}
                                    onClick={() => {
                                      showDialog(<ExpenseForm />, {
                                        dialogTitle: "New Expense Entry",
                                      });
                                    }}
                                  >
                                    Add Expenses
                                  </Button>
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "0.5rem",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "0.6875rem",
                                        fontWeight: 500,
                                        color: "text.secondary",
                                      }}
                                    >
                                      Total Expenses:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.6875rem",
                                        fontWeight: 500,
                                        color: "#666",
                                      }}
                                    >
                                      {formatCurrency(totalExpenses)}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.6875rem",
                                        fontWeight: 500,
                                        color: "text.secondary",
                                      }}
                                    >
                                      Unbilled Expenses:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: "0.6875rem",
                                        fontWeight: 500,
                                        color: "#666",
                                      }}
                                    >
                                      {formatCurrency(unbilledExpenses)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </CustomAccordion>
                      </Box>
                      {/* Fixed Matter Details Section */}
                      <Box sx={{ display: "flex", gap: "0.75rem" }}>
                        <Box sx={{ width: "100%" }}>
                          <CustomAccordion
                            expanded={matterExpand}
                            onChange={() => setMatterExpand(!matterExpand)}
                            title="Extra Section(s) Details"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "#fff",
                                minHeight: "250px",
                                p: "0.5rem",
                                gap: "0.75rem",
                              }}
                            >
                              {customFieldSections.length === 0 && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "grey.500",
                                    fontSize: "0.875rem",
                                    textAlign: "center",
                                    mt: 2,
                                  }}
                                >
                                  No details available
                                </Typography>
                              )}

                              {customFieldSections?.map((section, sIdx) =>
                                section.sectionName === "Matter Details" ||
                                section.sectionName ===
                                  "Billing preferences" ? null : (
                                  <Box
                                    key={sIdx}
                                    sx={{
                                      border: "0.0625rem solid #e5e5e5",
                                      borderRadius: "0.375rem",
                                      overflow: "hidden",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        p: "0.5rem",
                                        bgcolor: "#f9fafb",
                                        borderBottom: "0.0625rem solid #e5e5e5",
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "0.875rem",
                                        }}
                                      >
                                        {section.sectionName}
                                      </Typography>
                                    </Box>
                                    {section.fields.map((field, fIdx) => (
                                      <InfoRow
                                        key={fIdx}
                                        label={field.label || field.name}
                                        value={formatFieldValue(field)}
                                        fieldName={`${section.sectionName}_${field.name}`}
                                      />
                                    ))}
                                  </Box>
                                )
                              )}
                            </Box>
                          </CustomAccordion>
                        </Box>
                        {/* <Box sx={{ width: "30%" }}>
                          <CustomAccordion
                            expanded={timeLineExpend}
                            onChange={() => settimeLineExpend(!timeLineExpend)}
                            title="Time Line(s)"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.375rem",
                                p: "0.5rem",
                                minHeight: "150px",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "grey.500",
                                  fontSize: "0.875rem",
                                  textAlign: "center",
                                  mt: 2,
                                }}
                              >
                                No timelines available
                              </Typography>
                            </Box>
                          </CustomAccordion>
                        </Box> */}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: "35%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      <Box>
                        <CustomAccordion
                          expanded={contactExpand}
                          onChange={() => setContactExpand(!contactExpand)}
                          title="Connected Contacts"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.375rem",
                              p: "0.5rem",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.375rem",
                                borderRadius: "0.5rem",
                                border: "0.0625rem solid #e5e5e5",
                                p: "0.75rem",
                                height: contactDetailExpand
                                  ? "auto"
                                  : "fit-content",
                                transition: "height 0.2s",
                                backgroundColor: "#fff",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  width: "100%",
                                  gap: "0.75rem",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: "0.75rem",
                                    zIndex: 101,
                                  }}
                                >
                                  <UserAvatar height={"2rem"} width={"2rem"} />
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "0.125rem",
                                    }}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "0.875rem",
                                        fontWeight: 500,
                                        color: "text.primary",
                                      }}
                                    >
                                      {caseData?.case_related_person
                                        ? `${caseData?.case_related_person?.first_name}
                                      ${caseData?.case_related_person?.last_name}`
                                        : "Contact Name"}
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontSize: "0.75rem",
                                        fontWeight: 500,
                                        color: "text.secondary",
                                      }}
                                    >
                                      client
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setContactDetailExpand(
                                        !contactDetailExpand
                                      )
                                    }
                                    sx={{ p: "0.25rem" }}
                                  >
                                    <KeyboardArrowDownRounded
                                      sx={{ fontSize: "1rem" }}
                                    />
                                  </IconButton>
                                </Box>
                              </Box>
                              {contactDetailExpand && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Divider sx={{ mb: 0.5 }} />
                                  <InfoRow
                                    label="Phone"
                                    value={
                                      caseData?.case_related_person?.mobile
                                    }
                                    fieldName="phone"
                                    icon={Phone}
                                    color="primary.main"
                                  />
                                  <InfoRow
                                    label="Email"
                                    value={
                                      caseData?.case_related_person
                                        ?.reminder_mail
                                    }
                                    fieldName="email"
                                    icon={Email}
                                    color="secondary.main"
                                  />
                                  <InfoRow
                                    label="Address"
                                    value={
                                      caseData?.case_related_person?.address_1
                                    }
                                    fieldName="address"
                                    icon={LocationOn}
                                    color="error.main"
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CustomAccordion>
                      </Box>
                      <Box>
                        <CustomAccordion
                          expanded={matterExpand}
                          onChange={() => setMatterExpand(!matterExpand)}
                          title="Matter Details"
                        >
                          <InfoRow
                            label={"Practice Area"}
                            value={caseData?.practice_area?.name || "N/A"}
                            fieldName={"practice_area"}
                          />
                          <InfoRow
                            label={"Matter Description"}
                            value={caseData?.case_description || "N/A"}
                            fieldName={"case_description"}
                          />
                          <InfoRow
                            label={"Matter Responsible Attorney"}
                            value={
                              caseData?.case_responsible_attorney
                                ? `${caseData?.case_responsible_attorney?.first_name} ${caseData?.case_responsible_attorney?.last_name}`
                                : "N/A"
                            }
                            fieldName={"case_responsible_attorney"}
                          />
                          <InfoRow
                            label={"Matter Originating Attorney"}
                            value={
                              caseData?.case_originating_attorney
                                ? `${caseData?.case_originating_attorney?.first_name} ${caseData?.case_originating_attorney?.last_name}`
                                : "N/A"
                            }
                            fieldName={"case_originating_attorney"}
                          />
                        </CustomAccordion>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </Box>

        {/* Snackbar for copy feedback */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            sx={{ width: "100%", fontSize: "0.75rem" }}
          >
            Copied to clipboard!
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default CaseDashboard;
